import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { AxiosError } from 'axios';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../i18n/useTranslation';
import ErrorAlert from '../../../shared/alerts/error-alert';
import { logOut } from '../../../auth/services/auth.service';
import { USER_PANEL_PATHS } from '../../../shared/constants/breadcrumb.constants';
import { changePassword } from '../../client-panel/components/my-account-info/services/user-details.service';
import PasswordField from './password-field';
import { createChangePasswordValidationSchema } from './change-password.validation';
import { cardSx, pageSx, primaryButtonSx, secondaryButtonSx } from './password-form.styles';

const SERVER_ERROR_KEY_PREFIX = 'myAccountInfo.userDetails.errors.';

interface ChangePasswordFormValues {
    currentPassword: string;
    newPassword: string;
    repeatPassword: string;
}

const ChangePassword: React.FC = () => {
    const { t, i18n } = useTranslation('translation');
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [errorAlertIsOpen, setErrorAlertIsOpen] = useState<boolean>(false);
    const [errorTranslateCode, setErrorTranslateCode] = useState<string>('');

    // The server answers with a translation key when the user can fix the problem (wrong current password, new
    // password against the policy). Anything else - including a raw, untranslated message - stays generic.
    const getErrorTranslateCode = (error: unknown): string => {
        const data = error instanceof AxiosError ? error.response?.data : undefined;
        const isKnownKey = typeof data === 'string' && data.startsWith(SERVER_ERROR_KEY_PREFIX) && i18n.exists(data);
        return isKnownKey ? data : 'myAccountInfo.userDetails.passwordChangeError';
    };

    const validationSchema = useMemo(() => createChangePasswordValidationSchema(t), [t]);

    const goBackToUserPanel = () => navigate(USER_PANEL_PATHS[0].link);

    const formik = useFormik<ChangePasswordFormValues>({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            repeatPassword: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                await changePassword({ currentPassword: values.currentPassword, newPassword: values.newPassword });
                toast.success(t('myAccountInfo.userDetails.passwordChangedSuccess'));
                // The session was opened with the old password, so the user signs in again with the new one.
                logOut(navigate);
                navigate('/login');
            } catch (error) {
                setErrorTranslateCode(getErrorTranslateCode(error));
                setErrorAlertIsOpen(true);
            } finally {
                setLoading(false);
            }
        },
    });

    const getFieldProps = (name: keyof ChangePasswordFormValues) => ({
        id: name,
        value: formik.values[name],
        onChange: (value: string) => formik.setFieldValue(name, value),
        error: Boolean(formik.touched[name] && formik.errors[name]),
        helperText: (formik.touched[name] && formik.errors[name]) || '',
    });

    return (
        <Box sx={pageSx}>
            <ErrorAlert
                isOpen={errorAlertIsOpen}
                handleCloseDialog={setErrorAlertIsOpen}
                translateCode={errorTranslateCode}
            />
            <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={cardSx}>
                <Typography variant="h5" align="center">
                    {t('myAccountInfo.userDetails.changePassword')}
                </Typography>

                <PasswordField
                    {...getFieldProps('currentPassword')}
                    label={t('myAccountInfo.userDetails.currentPassword')}
                    autoComplete="current-password"
                />
                <PasswordField
                    {...getFieldProps('newPassword')}
                    label={t('myAccountInfo.userDetails.newPassword')}
                    autoComplete="new-password"
                />
                <PasswordField
                    {...getFieldProps('repeatPassword')}
                    label={t('myAccountInfo.userDetails.repeatPassword')}
                    autoComplete="new-password"
                />

                <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    disabled={loading}
                    sx={primaryButtonSx}
                    disableRipple
                >
                    {loading ? <CircularProgress size={22} color="inherit" /> : t('myAccountInfo.userDetails.save')}
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={goBackToUserPanel}
                    disabled={loading}
                    sx={secondaryButtonSx}
                    disableRipple
                >
                    {t('myAccountInfo.userDetails.cancel')}
                </Button>
            </Box>
        </Box>
    );
};

export default ChangePassword;
